import type { AuthPrivilegedCarrier } from '@ideon-api/sdk-internal'
import { carrierLabel } from './carrier-label'
import { MARKETS, lineLabel, marketFor, type Market } from './product-lines'

export type CarrierEntry = AuthPrivilegedCarrier

export interface CarrierFilters {
  q?: string
  productLine?: string
  market?: Market
  state?: string
}

export function parseCarrierFilters(params: URLSearchParams): CarrierFilters {
  const market = params.get('market')
  return {
    q: params.get('q') || undefined,
    productLine: params.get('product_line') || undefined,
    market: MARKETS.find((m) => m === market),
    state: params.get('state') || undefined,
  }
}

export function filterCarriers(list: CarrierEntry[], f: CarrierFilters): CarrierEntry[] {
  const q = f.q?.toLowerCase()
  return list.filter(
    (e) =>
      (!q || e.carrier.toLowerCase().includes(q) || carrierLabel(e.carrier).toLowerCase().includes(q)) &&
      (!f.productLine || e.productLine.toLowerCase() === f.productLine.toLowerCase()) &&
      (!f.market || marketFor(e.productLine) === f.market) &&
      (!f.state || e.states.includes(f.state))
  )
}

export function summarize(list: CarrierEntry[]) {
  return {
    definitions: list.length,
    carriers: new Set(list.map((e) => e.carrier.trim().toLowerCase())).size,
    flagged: list.filter((e) => e.flags.length > 0).length,
  }
}

export const issuerIdCount = (e: CarrierEntry): number =>
  new Set(e.stateIssuers.flatMap((s) => s.issuerIds)).size

export const accountCount = (e: CarrierEntry): number =>
  new Set(e.grants.map((g) => g.accountSlug).filter((s): s is string => s !== null)).size

export const productLineOptions = (list: CarrierEntry[]): { value: string; label: string }[] =>
  [...new Set(list.map((e) => e.productLine))].map((value) => ({ value, label: lineLabel(value) }))

export const stateOptions = (list: CarrierEntry[]): string[] =>
  [...new Set(list.flatMap((e) => e.states).filter((s) => s.trim() !== ''))].sort()

// A "*" grant reaches every account, so accountCount (named accounts only) reads 0 for it.
export const hasWildcardGrant = (e: CarrierEntry): boolean => e.grants.some((g) => g.accountSlug === null)
